// Client-side image compression to keep upload payloads small.
// Produces a base64 data URL <= ~500KB (target) using canvas re-encode.

const MAX_BYTES = 500 * 1024; // 500 KB target
const MAX_DIMENSION = 1800; // Cap longest side (px)

const bytesFromDataUrl = (dataUrl) => {
  // Approx byte size of the raw binary encoded in base64
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = (b64.match(/=+$/) || [""])[0].length;
  return (b64.length * 3) / 4 - padding;
};

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const loadImg = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

/**
 * Compress an image file to a JPEG data URL <= 500KB when possible.
 * Non-image files or unsupported types pass through as base64.
 */
export async function compressImageToBase64(file) {
  const originalDataUrl = await readFile(file);
  if (!file.type || !file.type.startsWith("image/")) {
    return originalDataUrl;
  }
  // GIFs and SVGs — skip re-encoding (loses animation/vectorness)
  if (file.type === "image/gif" || file.type === "image/svg+xml") {
    return originalDataUrl;
  }

  try {
    const img = await loadImg(originalDataUrl);
    let { width, height } = img;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    // Progressive quality reduction until under 500KB
    let quality = 0.88;
    let out = canvas.toDataURL("image/jpeg", quality);
    while (bytesFromDataUrl(out) > MAX_BYTES && quality > 0.35) {
      quality -= 0.1;
      out = canvas.toDataURL("image/jpeg", quality);
    }
    // Final downscale pass if still too large
    let dimScale = 1;
    while (bytesFromDataUrl(out) > MAX_BYTES && dimScale > 0.4) {
      dimScale -= 0.15;
      const c2 = document.createElement("canvas");
      c2.width = Math.round(width * dimScale);
      c2.height = Math.round(height * dimScale);
      c2.getContext("2d").drawImage(img, 0, 0, c2.width, c2.height);
      out = c2.toDataURL("image/jpeg", quality);
    }
    return out;
  } catch (e) {
    return originalDataUrl;
  }
}

/** Same helper for arbitrary files — returns base64 unchanged for non-image. */
export async function fileToBase64(file) {
  if (file.type && file.type.startsWith("image/")) {
    return compressImageToBase64(file);
  }
  return readFile(file);
}
